import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";
import { X, MapPin, User, StickyNote, Thermometer, Database, CheckCircle2, Store, Home, KeyRound } from "lucide-react";
import MarkLostMenu from "./MarkLostMenu";
import AppraisalReminderSeries from "./AppraisalReminderSeries";
import { PIPELINES, pipelineLabel } from "./PipelineAppraisalEditModal";
import { TEMPERATURES, labelFor as temperatureLabel } from "./AppraisalTemperatureModal";
import { TRANSACTION_TYPES } from "./AddSellerLeadModal";
import { updateLead, getLeadReminders, createLeadReminders, deleteLeadReminder } from "../../../api/lead";
import { parseAuAddress } from "../utils/auAddress";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

/**
 * The Edit Appraisal panel, for a seller lead that is not an appraisal yet.
 *
 * Christian Gravias, Monday 12988442847, 17 Oct 2026: "Use the Edit button
 * under Appraisal for New & Connected also". His screenshot is the appraisal
 * card's Edit Appraisal panel: the note and the reminder series at the top,
 * then the property.
 *
 * A New or Connected lead has no property record yet, so the price, beds and
 * commission sections of that panel have nothing to write to. Everything a
 * lead does have is here, in the same order and the same look:
 *
 *   Add a Note          appended to the lead's own thread (append_agent_note),
 *                       never a rewrite of it, the same as the Notes button
 *   Reminder series     booked against the lead, the same as the Notes button
 *   Contact             name, mobile, email
 *   Property            the address the lead came in with
 *   Sale or rental      Lead.transaction_type, as Add Lead sets it (Christian
 *                       Gravias, DOMA 13375194285, 26 Oct 2026: "Add it in the
 *                       edit appraisal section as well")
 *   Appraisal Pipeline  Off Market / On Market (Lead.market)
 *   Temperature         Cold / Warm / Hot, once the lead is Connected
 *
 * plus Add as Appraisal, Add to Database, Listed and Lost along the bottom,
 * like the appraisal panel's footer. One Save sends the whole lead update in a
 * single request, then books the reminders, so a failed booking can never
 * lose a note that has already saved.
 */

// The display stamp the lead's note thread is written with, kept identical to
// LeadNotesModal so a note written here reads the same everywhere.
const STAMP_FORMAT = "D/M/YYYY h:mm a";

const labelStyle = {
    fontFamily: "var(--font-sans)",
    fontWeight: 400,
    fontSize: "13.69px",
    lineHeight: "100%",
    color: "var(--c-on-muted)",
    verticalAlign: "middle",
};

const headingStyle = {
    fontFamily: "var(--font-sans)",
    fontWeight: 700,
    fontSize: "17.16px",
    lineHeight: "100%",
    color: "var(--c-on-strong)",
    verticalAlign: "middle",
};

const inputClass =
    "w-full bg-white border border-[#E4E6E9] rounded-[2.81px] px-4 py-3 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 transition-all placeholder:text-[#9CA3AF]";

const leadAddressOf = (lead) => lead?.property_address || lead?.owner_subject_address || "";

// "17 Jones Street, Thornbury VIC 3071", the house style the rest of the CRM
// writes an address in.
const joinAddress = ({ address, suburb, state, postal_code }) => {
    const tail = [suburb, state, postal_code].map((p) => String(p || "").trim()).filter(Boolean).join(" ");
    return [String(address || "").trim(), tail].filter(Boolean).join(", ");
};

const LeadAppraisalEditModal = ({
    isOpen,
    lead,
    stage,
    onClose,
    onSaved,
    onAddAsAppraisal,
    onAddToDatabase,
    onListed,
    onLost,
}) => {
    const { user } = useSelector((state) => state.auth || {});
    const [form, setForm] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    // Seeded per lead, not on every parent refresh, so a half-typed note is
    // never wiped out from under the agent.
    useEffect(() => {
        if (!isOpen || !lead) return;
        const parsed = parseAuAddress(leadAddressOf(lead));
        setReminders([]);
        setError("");
        setForm({
            name: lead.name || lead.full_name || "",
            phone: lead.phone || "",
            email: lead.email || "",
            second_owner_name: lead.second_owner_name || "",
            second_owner_phone: lead.second_owner_phone || "",
            second_owner_email: lead.second_owner_email || "",
            address: parsed.address,
            suburb: parsed.suburb,
            state: parsed.state,
            postal_code: parsed.postal_code,
            market: lead.market === "on_market" ? "on_market" : "off_market",
            transaction_type: lead.transaction_type === "rental" ? "rental" : "sale",
            temperature: String(lead.temperature || "").toLowerCase(),
            note: "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, lead?.id]);

    const leadId = lead?.id;
    const fetchBooked = useCallback(() => getLeadReminders(leadId), [leadId]);
    const deleteBooked = useCallback((kind, id) => deleteLeadReminder(leadId, kind, id), [leadId]);

    if (!isOpen || !lead || !form) return null;

    const setField = (name, value) => {
        setError("");
        setForm((prev) => ({ ...prev, [name]: value }));
    };
    const handleChange = (e) => setField(e.target.name, e.target.value);

    const notes = Array.isArray(lead.agent_notes) ? lead.agent_notes : [];
    const latestNote = notes[notes.length - 1];
    // Temperature belongs to a lead once it has been called (Christian
    // Gravias, Monday 12988442847, 16 Oct 2026: "Add a temperature for
    // connected").
    const showTemperature = stage === "connected" || stage === "long_term";

    const currentMarket = lead.market === "on_market" ? "on_market" : "off_market";
    const marketChanged = form.market !== currentMarket;
    const currentTransaction = lead.transaction_type === "rental" ? "rental" : "sale";
    const transactionChanged = form.transaction_type !== currentTransaction;
    const transactionLabel = (v) => TRANSACTION_TYPES.find((t) => t.value === v)?.label || v;
    const currentTemperature = String(lead.temperature || "").toLowerCase();
    const temperatureChanged = showTemperature && form.temperature !== currentTemperature;
    const original = parseAuAddress(leadAddressOf(lead));
    const addressChanged = ["address", "suburb", "state", "postal_code"].some(
        (k) => String(form[k] || "").trim() !== String(original[k] || "").trim()
    );

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (saving) return;
        const typedNote = form.note.trim();
        const phone = form.phone.trim();
        const email = form.email.trim();

        if (!phone && !email) {
            setError("Leave a mobile or an email, or nobody can contact this lead.");
            return;
        }
        // The same rule as the appraisal panel: a move nobody explained is
        // what the next agent cannot work with.
        if (temperatureChanged && !typedNote) {
            setError("Add a note saying why the temperature changed.");
            return;
        }
        if (marketChanged && !typedNote) {
            setError("Add a note saying why it moved pipeline.");
            return;
        }

        const payload = {
            name: form.name.trim(),
            phone,
            email,
            second_owner_name: form.second_owner_name.trim(),
            second_owner_phone: form.second_owner_phone.trim(),
            second_owner_email: form.second_owner_email.trim(),
        };
        if (addressChanged) {
            const address = joinAddress(form);
            // Both fields, as the lead's own edit form writes them, so every
            // screen that reads either one shows the corrected address.
            payload.property_address = address;
            payload.owner_subject_address = address;
        }
        const moves = [];
        if (temperatureChanged) {
            payload.temperature = form.temperature;
            moves.push(`Moved from ${temperatureLabel(currentTemperature)} to ${temperatureLabel(form.temperature)}.`);
        }
        // A correction rather than a move, so no note is required; the change is
        // still written into the thread so the next agent can see who made it.
        if (transactionChanged) {
            payload.transaction_type = form.transaction_type;
            moves.push(`Changed from ${transactionLabel(currentTransaction)} to ${transactionLabel(form.transaction_type)}.`);
        }
        if (marketChanged) {
            payload.market = form.market;
            moves.push(`Moved from ${pipelineLabel(currentMarket)} to ${pipelineLabel(form.market)} pipeline.`);
        }
        const content = [...moves, typedNote].filter(Boolean).join(" ");
        if (content) {
            payload.append_agent_note = {
                timestamp: moment().format(STAMP_FORMAT),
                created_at: moment().toISOString(),
                author: user?.name || user?.email || "Agent",
                content,
            };
        }

        setSaving(true);
        let saved;
        try {
            const res = await updateLead(lead.id, payload);
            const body = res?.data && typeof res.data === "object" ? res.data : {};
            const { append_agent_note: _sent, ...fields } = payload;
            saved = {
                ...lead,
                ...fields,
                full_name: fields.name,
                agent_notes: Array.isArray(body.agent_notes)
                    ? body.agent_notes
                    : content
                    ? [...notes, payload.append_agent_note]
                    : notes,
                ...(body.last_contact_date ? { last_contact_date: body.last_contact_date } : {}),
            };
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save the lead.");
            setSaving(false);
            return;
        }

        let booked = 0;
        if (reminders.length) {
            try {
                await createLeadReminders(
                    lead.id,
                    reminders.map((entry) => ({ date: entry.date.toISOString(), reason: entry.reason })),
                    typedNote
                );
                booked = reminders.length;
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Saved, but the reminders could not be booked.");
            }
        }
        setSaving(false);
        toast.success(booked ? `Lead updated and ${booked} reminder${booked === 1 ? "" : "s"} booked.` : "Lead updated.");
        onSaved && onSaved(saved);
        onClose();
    };

    const section = (icon, title, children) => (
        <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
            <div className="flex items-center gap-2 text-[#55C5D0]">
                {icon}
                <h3 style={headingStyle}>{title}</h3>
            </div>
            {children}
        </div>
    );

    const field = (name, label, placeholder, type = "text") => (
        <div className="space-y-3 px-1">
            <label style={labelStyle} htmlFor={`lead-appraisal-${name}`}>
                {label}
            </label>
            <input
                id={`lead-appraisal-${name}`}
                name={name}
                type={type}
                value={form[name]}
                onChange={handleChange}
                placeholder={placeholder}
                className={inputClass}
            />
        </div>
    );

    return (
        // No close on a background click: a typed note is too easy to lose.
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in font-sans text-[#353B48]">
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Edit Appraisal"
                className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
            >
                <div className="px-6 py-5 bg-[#F7F9FB] flex items-center justify-between" style={{ borderBottom: "0.45px solid #D3D3D3" }}>
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-[#1F2937]">Edit Appraisal</h2>
                        <p className="text-[12px] text-[#6B7280] mt-0.5">
                            {stage === "connected" ? "Connected" : stage === "long_term" ? "Long Term" : "New"} lead, not an appraisal yet
                        </p>
                    </div>
                    <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#9CA3AF]">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar">
                    {section(
                        <StickyNote className="w-5 h-5" />,
                        "Add a Note",
                        <>
                            {latestNote?.content && (
                                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                                    <StickyNote className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-[12px] text-gray-700 whitespace-pre-wrap break-words">{latestNote.content}</p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">
                                            Last note by {latestNote.author || "Unknown"}
                                            {latestNote.created_at
                                                ? ` · ${formatMelbourneDateTime(latestNote.created_at)}`
                                                : latestNote.timestamp
                                                ? ` · ${latestNote.timestamp}`
                                                : ""}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="space-y-3 px-1">
                                <label style={labelStyle} htmlFor="lead-appraisal-note">
                                    New note {(temperatureChanged || marketChanged) && <span className="text-red-500">*</span>}
                                </label>
                                <textarea
                                    id="lead-appraisal-note"
                                    name="note"
                                    rows={3}
                                    value={form.note}
                                    onChange={handleChange}
                                    placeholder={
                                        temperatureChanged || marketChanged
                                            ? "Why are you moving it? e.g. Owner has decided to list before Christmas."
                                            : "What has happened with this lead?"
                                    }
                                    className={inputClass}
                                />
                            </div>
                            <div className="px-1">
                                <AppraisalReminderSeries
                                    key={lead.id}
                                    fetchBooked={fetchBooked}
                                    deleteBooked={deleteBooked}
                                    value={reminders}
                                    onChange={setReminders}
                                    pendingLabel="Saving with this note"
                                />
                            </div>
                        </>
                    )}

                    {section(
                        <User className="w-5 h-5" />,
                        "Contact",
                        <>
                            {field("name", "Name", "Who the lead is")}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {field("phone", "Mobile", "0412 345 678")}
                                {field("email", "Email", "name@example.com", "email")}
                            </div>
                            {/* Jointly owned properties (George Bottas CRM-2026-00114). */}
                            {field("second_owner_name", "Second Owner", "Name, if there is a second owner")}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {field("second_owner_phone", "Second Owner Mobile", "0412 345 678")}
                                {field("second_owner_email", "Second Owner Email", "name@example.com", "email")}
                            </div>
                        </>
                    )}

                    {section(
                        <MapPin className="w-5 h-5" />,
                        "Property",
                        <>
                            {field("address", "Property Address", "17 Jones Street")}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {field("suburb", "Suburb", "Thornbury")}
                                {field("state", "State", "VIC")}
                                {field("postal_code", "Postcode", "3071")}
                            </div>
                        </>
                    )}

                    {section(
                        <KeyRound className="w-5 h-5" />,
                        "Sale or Rental",
                        <>
                            <div className="grid grid-cols-2 gap-2 px-1" role="radiogroup" aria-label="Sale or rental">
                                {TRANSACTION_TYPES.map((t) => {
                                    const isSelected = t.value === form.transaction_type;
                                    return (
                                        <button
                                            key={t.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            onClick={() => setField("transaction_type", t.value)}
                                            className={`rounded-xl border-2 px-3 py-3 text-center text-[14px] font-bold transition-all ${
                                                isSelected
                                                    ? "bg-[#55C5D0] text-white border-[#55C5D0]"
                                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-[#9CA3AF] px-1">
                                {form.transaction_type === "rental"
                                    ? "Rental leads are not counted in the sales analytics."
                                    : "Counted in the sales analytics."}
                            </p>
                        </>
                    )}

                    {section(
                        <Store className="w-5 h-5" />,
                        "Appraisal Pipeline",
                        <>
                            <div className="grid grid-cols-2 gap-2 px-1">
                                {PIPELINES.map((p) => {
                                    const isSelected = p.value === form.market;
                                    return (
                                        <button
                                            key={p.value}
                                            type="button"
                                            aria-pressed={isSelected}
                                            aria-label={`Move to ${p.label}`}
                                            onClick={() => setField("market", p.value)}
                                            className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${isSelected ? p.active : p.idle}`}
                                        >
                                            <span className="block text-[14px] font-bold">{p.label}</span>
                                            <span className="block text-[10px] font-medium opacity-80 mt-0.5">
                                                {p.value === currentMarket ? "Current" : p.hint}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-[#9CA3AF] px-1">
                                {marketChanged
                                    ? `Saving moves this lead to ${pipelineLabel(form.market)}. Say why in the note above.`
                                    : `Currently ${pipelineLabel(currentMarket)}. Pick the other one to move it when you save.`}
                            </p>
                        </>
                    )}

                    {showTemperature &&
                        section(
                            <Thermometer className="w-5 h-5" />,
                            "Temperature",
                            <>
                                <div className="grid grid-cols-3 gap-2 px-1">
                                    {TEMPERATURES.map((t) => {
                                        const isSelected = t.value === form.temperature;
                                        return (
                                            <button
                                                key={t.value}
                                                type="button"
                                                aria-pressed={isSelected}
                                                aria-label={`Move to ${t.label}`}
                                                onClick={() => setField("temperature", t.value)}
                                                className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${isSelected ? t.active : t.idle}`}
                                            >
                                                <span className="block text-[14px] font-bold">{t.label}</span>
                                                <span className="block text-[10px] font-medium opacity-80 mt-0.5">
                                                    {t.value === currentTemperature ? "Current" : t.hint}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-[#9CA3AF] px-1">
                                    {temperatureChanged
                                        ? `Saving moves this lead from ${temperatureLabel(currentTemperature)} to ${temperatureLabel(
                                              form.temperature
                                          )}. Say why in the note above.`
                                        : `Currently ${temperatureLabel(currentTemperature)}.`}
                                </p>
                            </>
                        )}
                </form>

                <div className="px-6 md:px-8 py-5 border-t border-[#F3F4F6] flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#FDFDFD]">
                    <div className="flex flex-wrap items-center gap-3">
                        {onAddAsAppraisal && (
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    onAddAsAppraisal();
                                }}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#55C5D0] text-white font-bold rounded-xl hover:bg-[#45b5c0] transition-all text-[14px]"
                            >
                                <Home className="w-4 h-4" />
                                Add as Appraisal
                            </button>
                        )}
                        {onAddToDatabase && (
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    onAddToDatabase();
                                }}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E4E6E9] text-[#353B48] font-bold rounded-xl hover:bg-gray-50 transition-all text-[14px]"
                            >
                                <Database className="w-4 h-4 text-[#55C5D0]" />
                                Add to Database
                            </button>
                        )}
                        {onListed && (
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    onListed();
                                }}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#55C5D0] text-[#2b9aa4] font-bold rounded-xl hover:bg-[#eefafb] transition-all text-[14px]"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                Listed
                            </button>
                        )}
                        {onLost && (
                            <MarkLostMenu
                                onSelect={(reason, note) => {
                                    onClose();
                                    onLost(reason, note);
                                }}
                                align="left"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E4E6E9] text-[#6B7280] font-bold rounded-xl hover:bg-gray-50 hover:text-[#c8392f] transition-all text-[14px]"
                            />
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-4">
                        {error && <p className="text-[12px] font-bold text-red-500">{error}</p>}
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="px-8 py-3.5 border border-[#E4E6E9] text-[#6B7280] font-bold rounded-xl hover:bg-gray-50 transition-all text-[15px] min-w-[120px] disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="px-10 py-3.5 bg-[#55C5D0] hover:bg-[#45b5c0] text-white font-bold rounded-xl shadow-lg shadow-[#55C5D0]/20 transition-all text-[15px] min-w-[140px] disabled:opacity-60"
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadAppraisalEditModal;
